import type React from 'react';
import {
  Combobox,
  ComboboxEmptyState,
  DetailedIconHome,
  FormField,
  FormHelp,
  Grid,
  Heading,
  Input,
  Label,
  Select,
} from '@zillow/constellation';
import { Controller, useFormContext } from 'react-hook-form';
import { MIN_AUTOCOMPLETE_ADDRESS_LENGTH, useAddressAutocomplete } from '../../hooks/useAddressAutocomplete';
import type { AddressResponse } from '../../models';

export function EditPropertyAddress({ listingAlias }: { listingAlias?: string }) {
  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<{
    addressString: string;
    propertyTypeCode: number;
    updatedAddress?: Partial<AddressResponse>;
    unit?: string;
  }>();
  const propertyTypeCode = watch('propertyTypeCode');
  const [partialAddress, setPartialAddress, queryData] = useAddressAutocomplete({});
  return (
    <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" style={{ minHeight: '100%' }}>
      <Grid gridColumnEnd={{ default: 'span 12', lg: 'span 8', xl_gte: 'span 7' }}>
        <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" marginTop="xl">
          <Grid gridColumnEnd={{ default: 'span 1', lg: 'span 2' }}></Grid>
          <Grid gridColumnEnd={{ default: 'span 10', lg: 'span 8' }}>
            <DetailedIconHome marginY="md" />
            <Heading level={4} as="h2" fontFamily="serif">
              Edit your property address
            </Heading>
            <Controller
              name="addressString"
              rules={{
                required: 'A USPS validated address is required',
              }}
              render={({ field: { onChange, value } }) => (
                <FormField
                  marginY="sm"
                  label={<Label id="property-address-label">Street address</Label>}
                  control={
                    <Combobox
                      appearance="input"
                      focusFirstOption={true}
                      options={
                        queryData?.data?.data?.response?.addresses?.map((address) => ({
                          ...address,
                          value: address.name,
                          label: address.name,
                        })) ?? []
                      }
                      onOptionSelect={
                        ((newAddress: AddressResponse) => {
                          onChange(newAddress.name);
                          setValue('updatedAddress', {
                            propertyId: newAddress.propertyId,
                            street: newAddress.street,
                            city: newAddress.city,
                            state: newAddress.state,
                            zip: newAddress.zip,
                          });
                          setPartialAddress('');
                        }) as any
                      }
                      loading={queryData.isFetching}
                      renderInput={({ ...rest }) => {
                        return (
                          <Input
                            {...rest}
                            value={value || partialAddress}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              onChange('');
                              setValue('updatedAddress', undefined);
                              setPartialAddress(e.target.value);
                            }}
                            aria-labelledby="property-address-label"
                          />
                        );
                      }}
                      renderEmptyState={() =>
                        partialAddress.length >= MIN_AUTOCOMPLETE_ADDRESS_LENGTH && queryData?.data ? (
                          <ComboboxEmptyState>No address found</ComboboxEmptyState>
                        ) : null
                      }
                    />
                  }
                  description={errors.addressString && <FormHelp>{errors.addressString.message}</FormHelp>}
                  error={!!errors.addressString}
                />
              )}
            />

            <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="sm">
              <Grid gridColumnEnd={{ default: 'span 12', lg: 'span 6' }}>
                <Controller
                  name="propertyTypeCode"
                  render={({ field }) => {
                    return (
                      <FormField
                        label={<Label>Property type</Label>}
                        control={
                          <Select
                            {...field}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                              const selectedValue = Number(e.currentTarget.value);
                              field.onChange(selectedValue);
                              if (selectedValue === 6) {
                                setValue('unit', undefined);
                              }
                            }}
                          >
                            <option value={6}>House</option>
                            <option value={5}>Apartment/condo</option>
                            <option value={7}>Townhouse</option>
                          </Select>
                        }
                        disabled={!!listingAlias}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid gridColumnEnd={{ default: 'span 12', lg: 'span 6' }}>
                {propertyTypeCode !== 6 && (
                  <Controller
                    name="unit"
                    render={({ field }) => {
                      return (
                        <FormField
                          label={<Label>Unit number</Label>}
                          control={<Input {...field} />}
                          description={
                            <FormHelp>If the property doesn’t have a unit number, leave this field blank</FormHelp>
                          }
                          disabled={!!listingAlias}
                        />
                      );
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid gridColumnEnd={{ default: 'span 1', lg: 'span 2' }}></Grid>
        </Grid>
      </Grid>
      <Grid gridColumnEnd={{ default: 'span 12', lg: 'span 4', xl_gte: 'span 5' }}></Grid>
    </Grid>
  );
}
